import React, { useMemo, useState, useEffect } from "react"
import TableContainer from "components/Common/TableContainer"
import {
  CardBody,
  Card,
  Row,
  Col,
  Form,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Label,
  Input,
  Container,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import Breadcrumbs from "../../../components/Common/Breadcrumb"

// Redux
import { useDispatch, useSelector } from "react-redux"
import {
  fetchEventTypes,
  updateEventType,
  addEventType,
  deleteEventType
} from "toolkitStore/eventTypeSlice"

function EventType() {
  const dispatch = useDispatch()

  // Local State
  const [isRight, setIsRight] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteData, setDeleteData] = useState(null)

  // Redux State
  const eventTypesState = useSelector(state => state.eventTypes)
  
  // Ensure data is always an array
  const data = useMemo(() => eventTypesState?.data || [], [eventTypesState?.data])

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchEventTypes())
  }, [dispatch])

  /* ---------------- Handlers ---------------- */

  const toggleRightCanvas = () => {
    setIsRight(!isRight)
    if (isRight) setSelectedData(null) 
  }

  const toggleDeleteModal = () => setModalOpen(!modalOpen)

  const handleDelete = async () => {
    if (deleteData?._id) {
      await dispatch(deleteEventType(deleteData._id))
      toggleDeleteModal()
    }
  }

  /* ---------------- Table Columns ---------------- */

  const columns = useMemo(() => [
    {
      Header: "Event Type",
      accessor: "name",
      // Adding an ID helps the internal TableContainer sortBy logic
      id: "name", 
    },
    {
      Header: "Description",
      accessor: "description",
      id: "description",
      // Optional: handle long text
      Cell: ({ value }) => value || <span className="text-muted">No description</span>
    },
    {
      Header: "Actions",
      id: "actions",
      disableFilters: true,
      disableSortBy: true, // Prevents sorting on action buttons
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            color="warning"
            size="sm"
            onClick={() => {
              setSelectedData(row.original)
              setIsRight(true)
            }}
          >
            <i className="mdi mdi-pencil" /> Edit
          </Button>

          <Button
            color="danger"
            size="sm"
            onClick={() => {
              setDeleteData(row.original)
              toggleDeleteModal()
            }}
          >
            <i className="mdi mdi-trash-can" /> Delete
          </Button>
        </div>
      )
    }
  ], [])

  /* ---------------- Formik Validation ---------------- */

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedData?.name || "",
      description: selectedData?.description || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter an event type name"),
      description: Yup.string().max(200, "Description too long"),
    }),
    onSubmit: async (values) => {
      if (selectedData) {
        await dispatch(updateEventType({
          id: selectedData._id,
          payload: values
        }))
      } else {
        await dispatch(addEventType(values))
      }
      setIsRight(false)
      validation.resetForm()
    }
  })

  return (
    <Container fluid className="page-content">
      <Breadcrumbs title="Events" breadcrumbItem="Event Management" />

      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center border-bottom pb-3 mb-3">
                <h5 className="mb-0 card-title flex-grow-1 text-primary">
                  Event Types List
                </h5>
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedData(null)
                    setIsRight(true)
                  }}
                >
                  <i className="mdi mdi-plus me-1" /> Add New Event Type
                </Button>
              </div>

              {/* CRITICAL FIX: 
                Passing a 'key' forced TableContainer to re-initialize 
                when data is loaded from the API.
              */}
              {data && data.length > 0 ? (
                <TableContainer
                  key={data.length} 
                  columns={columns}
                  data={data}
                  isGlobalFilter={true}
                  customPageSize={10}
                  className="custom-header-css"
                />
              ) : (
                <div className="text-center p-5">
                   <i className="mdi mdi-database-off display-4 text-muted"></i>
                   <h5 className="mt-2">No Event Types Found</h5>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Side Panel */}
      <Offcanvas isOpen={isRight} direction="end" toggle={toggleRightCanvas}>
        <OffcanvasHeader toggle={toggleRightCanvas}>
          {selectedData ? "Edit Event Type" : "Add Event Type"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
            <div className="mb-3">
              <Label className="form-label">Event Type Name</Label>
              <Input
                name="name"
                type="text"
                placeholder="Enter event name"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.name || ""}
                invalid={!!(validation.touched.name && validation.errors.name)}
              />
              <FormFeedback>{validation.errors.name}</FormFeedback>
            </div>
            <div className="mb-3">
                  <Label className="form-label">Description</Label>
                  <Input
                    name="description"
                    type="textarea" // Use textarea for better UX
                    rows="4"
                    placeholder="Enter event description"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.description || ""}
                    invalid={!!(validation.touched.description && validation.errors.description)}
                  />
                  <FormFeedback>{validation.errors.description}</FormFeedback>
                </div>
                
            <div className="d-flex gap-2">
              <Button type="submit" color="primary">Save</Button>
              <Button color="secondary" outline onClick={toggleRightCanvas}>Cancel</Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>

      {/* Delete Modal */}
      <Modal isOpen={modalOpen} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete <strong>{deleteData?.name}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>Delete</Button>
          <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

export default EventType